import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartjsComponent } from '@coreui/angular-chartjs';
import { ChartData, ChartOptions } from 'chart.js';
import { ApiUsersService } from '../../../../services/users/api/api-users.service';
import { AlertsService } from '../../../../services/alerts.service';
import { UsersTransactionsStatisticsResultsResponse } from '../../../../models/users/responses/api-users-transactions-statistics-response.model';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_SECONDS = 86400;
const SEVEN_DAYS = 7;

interface SeriesDef {
  key: keyof DaySeries;
  label: string;
  color: string;
}

interface DaySeries {
  freeCredits: number;
  leagueReward: number;
  missionReward: number;
  tournamentReward: number;
  levelReward: number;
  news: number;
}

@Component({
  selector: 'app-users-statistics-tab',
  templateUrl: './users-statistics-tab.component.html',
  standalone: true,
  imports: [CommonModule, ChartjsComponent]
})
export class UsersStatisticsTabComponent {

  isLoading: boolean = false;
  isLoaded: boolean = false;

  readonly series: SeriesDef[] = [
    { key: 'freeCredits', label: 'Free Credits', color: 'rgba(50, 110, 200, 1)' },
    { key: 'leagueReward', label: 'League Reward', color: 'rgba(40, 167, 69, 1)' },
    { key: 'missionReward', label: 'Mission Reward', color: 'rgba(220, 53, 69, 1)' },
    { key: 'tournamentReward', label: 'Tournament Reward', color: 'rgba(255, 159, 64, 1)' },
    { key: 'levelReward', label: 'Level Reward', color: 'rgba(153, 102, 255, 1)' },
    { key: 'news', label: 'News', color: 'rgba(23, 162, 184, 1)' }
  ];

  averages: { [key: string]: number } = {};

  transactionsTimestamps: number[] = [];

  transactionsChartData: ChartData = { labels: [], datasets: [] };
  transactionsChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 60, bottom: 40 } },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          title: (items) => {
            const t = this.transactionsTimestamps[items[0].dataIndex];
            if (t === undefined) return '';
            const d = new Date(t * 1000);
            return `${DAY_LABELS[d.getUTCDay()]} ${d.getUTCDate().toString().padStart(2, '0')}/${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value, index) => {
            const t = this.transactionsTimestamps[index];
            if (t === undefined) return '';
            return DAY_LABELS[new Date(t * 1000).getUTCDay()];
          }
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: { beginAtZero: true, ticks: { precision: 0 } }
    },
    elements: {
      line: { tension: 0.3 },
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 }
    }
  };

  constructor(
    private apiUsersService: ApiUsersService,
    private alertsService: AlertsService
  ) {}

  load(): void {
    if (this.isLoaded || this.isLoading) return;
    this.refresh();
  }

  refresh(): void {
    this.isLoading = true;

    this.apiUsersService.getUsersTransactionsStatistics().then(
      (response) => {
        this.isLoading = false;
        this.isLoaded = true;
        this.buildTransactionsChart(response);
      },
      error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading users statistics : ' + error);
      }
    );
  }

  private buildTransactionsChart(response: UsersTransactionsStatisticsResultsResponse): void {
    const byDay = new Map<number, DaySeries>();
    for (const s of response.results) {
      const dayStart = Math.floor(s.dayTimestamp / DAY_SECONDS) * DAY_SECONDS;
      byDay.set(dayStart, {
        freeCredits: s.freeCreditsCount,
        leagueReward: s.leagueRewardCount,
        missionReward: s.missionRewardCount,
        tournamentReward: s.tournamentRewardCount,
        levelReward: s.levelRewardCount,
        news: s.newsCount
      });
    }

    const endDay = Math.floor(Date.now() / 1000 / DAY_SECONDS) * DAY_SECONDS;
    const startDay = endDay - (SEVEN_DAYS - 1) * DAY_SECONDS;

    const timestamps: number[] = [];
    const values: { [key: string]: number[] } = {};
    for (const def of this.series) values[def.key] = [];

    for (let t = startDay; t <= endDay; t += DAY_SECONDS) {
      timestamps.push(t);
      const entry = byDay.get(t);
      for (const def of this.series) {
        values[def.key].push(entry ? entry[def.key] : 0);
      }
    }

    this.transactionsTimestamps = timestamps;

    this.averages = {};
    for (const def of this.series) {
      const arr = values[def.key];
      this.averages[def.key] = arr.length > 0 ? Math.round(arr.reduce((sum, v) => sum + v, 0) / arr.length) : 0;
    }

    this.transactionsChartData = {
      labels: timestamps.map((_, i) => i.toString()),
      datasets: this.series.map(def => ({
        label: def.label,
        data: values[def.key],
        backgroundColor: def.color.replace(', 1)', ', 0.15)'),
        borderColor: def.color,
        pointHoverBackgroundColor: def.color,
        borderWidth: 2,
        fill: false
      }))
    };
  }
}
